import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { bookingAPI } from '../config/api';
import { FaWhatsapp } from 'react-icons/fa';
import { FiCalendar, FiHome, FiDollarSign, FiCheckCircle, FiX, FiCheck } from 'react-icons/fi';

// Effective amount-paid helper — mirrors backend logic so the dashboard can
// show outstanding immediately, before the per-booking outstanding API call.
const computeGrandTotal = (b) => {
  if (b.grand_total) return Number(b.grand_total);
  return Number((b.total_amount || 0) +
                (b.security_deposit_total || b.security_deposit || 0));
};

const effectivePaid = (b) => {
  const stored = Number(b.amount_paid || 0);
  if (stored > 0) return stored;
  // No stored amount but status indicates payment happened — fall back:
  //  - 'paid'/'completed' → full grand_total
  //  - 'partial' → half of grand_total (50% advance is the default scheme)
  //  - 'half' payment_type also implies half
  const status = (b.payment_status || '').toLowerCase();
  if (['paid', 'completed'].includes(status)) return computeGrandTotal(b);
  if (status === 'partial' || b.payment_type === 'half') {
    return Math.ceil(computeGrandTotal(b) / 2);
  }
  return 0;
};

// Derive the displayed payment status from the actual paid vs total ratio.
// Backend's stored value may be stale (e.g. set to 'paid' but amount_paid is
// only 50% of grand_total). Trust the numbers, not the label.
const derivedStatus = (b) => {
  const total = computeGrandTotal(b);
  const paid = effectivePaid(b);
  if (total <= 0) return (b.payment_status || 'pending').toLowerCase();
  if (paid <= 0) return 'pending';
  if (paid >= total) return 'completed';
  return 'partial';
};

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [error, setError] = useState(null);
  // After successful BALANCE payment — capture the snapshot we used to build
  // the WhatsApp receipt for ops team forwarding.
  const [balanceSuccess, setBalanceSuccess] = useState(null); // { booking, paymentId, paidNow, balancePaidAt }
  const navigate = useNavigate();

  const fetchBookings = () =>
    bookingAPI.getMyBookings()
      .then(res => setBookings(res.data || []))
      .catch(() => {});

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    setUser(JSON.parse(stored));
    fetchBookings();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handlePayBalance = async (booking) => {
    setError(null);
    setPayingId(booking.id);
    try {
      const orderRes = await api.post('/payments/create-balance-order', { booking_id: booking.id });
      const { order_id, key_id, outstanding } = orderRes.data;

      if (!window.Razorpay) {
        setError('Payment gateway not loaded. Please refresh the page.');
        setPayingId(null);
        return;
      }

      const options = {
        key: key_id,
        amount: outstanding * 100,
        currency: 'INR',
        name: 'Royal Garden Villas',
        description: `Outstanding balance — Booking ${booking.id.slice(0, 8)}`,
        order_id,
        handler: async (response) => {
          try {
            await api.post('/payments/verify-balance', {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: booking.id,
            });
            // Snapshot booking + payment data BEFORE refetch so the receipt
            // we build matches what the guest just paid.
            setBalanceSuccess({
              booking,
              paymentId: response.razorpay_payment_id,
              paidNow: outstanding,
              balancePaidAt: new Date().toISOString(),
            });
            await fetchBookings();
            setPayingId(null);
          } catch {
            setError('Payment verification failed. Please contact us at +91 9820088292.');
            setPayingId(null);
          }
        },
        prefill: {
          name: user?.full_name || '',
          email: user?.email || '',
          contact: user?.phone || '',
        },
        theme: { color: '#F57C20' },
        modal: { ondismiss: () => setPayingId(null) },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start balance payment.');
      setPayingId(null);
    }
  };

  // Build comprehensive WhatsApp receipt for balance-payment confirmation.
  // Includes EVERYTHING ops needs: guest details, dates of original advance &
  // current balance payment, villa & add-on breakdown, prices & payment IDs.
  const buildBalanceReceipt = (snap) => {
    const { booking: b, paymentId, paidNow, balancePaidAt } = snap;
    const fmt = (d) => {
      try { return d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''; }
      catch { return d || ''; }
    };
    const fmtDateTime = (d) => {
      try { return d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''; }
      catch { return d || ''; }
    };
    const ref = (b.id || '').slice(0, 8).toUpperCase();
    const grand = computeGrandTotal(b);
    const villaName = b.villa_name || b.villa_id || 'Villa';
    const nights = b.nights || (() => {
      try {
        const ci = new Date(b.check_in); const co = new Date(b.check_out);
        return Math.max(1, Math.round((co - ci) / (1000 * 60 * 60 * 24)));
      } catch { return 1; }
    })();
    const adults = b.adults || b.num_adults || 0;
    const kids = b.kids || b.num_kids || 0;
    const lines = [];
    lines.push(`✅ *BALANCE PAYMENT RECEIVED — Royal Garden Villas*`);
    lines.push(`🆔 *Booking Ref:* ${ref}`);
    lines.push('');
    lines.push(`👤 *Guest:* ${b.guest_name || user?.full_name || '—'}`);
    lines.push(`📧 *Email:* ${b.guest_email || user?.email || '—'}`);
    lines.push(`📞 *Phone:* ${b.guest_phone || user?.phone || '—'}`);
    lines.push('');
    lines.push(`🏠 *Villa:* ${villaName}`);
    lines.push(`📅 *Check-in:* ${fmt(b.check_in)}`);
    lines.push(`📅 *Check-out:* ${fmt(b.check_out)}`);
    lines.push(`🌙 *Nights:* ${nights}`);
    lines.push(`👥 *Guests:* ${adults} adult${adults !== 1 ? 's' : ''}${kids > 0 ? `, ${kids} kid${kids !== 1 ? 's' : ''}` : ''}`);
    lines.push('');
    lines.push(`💰 *Pricing Breakdown:*`);
    if (Array.isArray(b.nightly_details) && b.nightly_details.length) {
      b.nightly_details.forEach((n) => {
        const rate = n.final_rate || n.rate || 0;
        const orig = Number(n.original_rate) || 0;
        const saved = Number(n.saved) || 0;
        const pct = n.discount_applied && orig ? Math.round((saved * 100) / orig) : 0;
        const tag = n.discount_applied ? ` (${pct}% off)` : '';
        lines.push(`• Night ${n.night || ''} · ${n.date || ''} — Rs.${Number(rate).toLocaleString()}${tag}`);
      });
    } else if (b.total_amount) {
      lines.push(`• Room (${nights} night${nights !== 1 ? 's' : ''}): Rs.${Number(b.total_amount).toLocaleString()}`);
    }
    if (b.extra_guest_charge > 0) lines.push(`• Extra guest charges: Rs.${Number(b.extra_guest_charge).toLocaleString()}`);
    if (b.maid_charge > 0) lines.push(`• Extra Butler Service${b.maid_days ? ` (${b.maid_days} day${b.maid_days !== 1 ? 's' : ''})` : ''}: Rs.${Number(b.maid_charge).toLocaleString()}`);
    if (b.bbq_charge > 0) lines.push(`• BBQ Service${b.bbq_meals ? ` (${b.bbq_meals} meal${b.bbq_meals !== 1 ? 's' : ''})` : ''}: Rs.${Number(b.bbq_charge).toLocaleString()}`);
    if (b.bonfire_charge > 0) lines.push(`• Bonfire${b.bonfire_sessions ? ` (${b.bonfire_sessions} session${b.bonfire_sessions !== 1 ? 's' : ''})` : ''}: Rs.${Number(b.bonfire_charge).toLocaleString()}`);
    if (b.security_deposit) lines.push(`• Refundable deposit: Rs.${Number(b.security_deposit).toLocaleString()}`);
    if (b.coupon_code && b.coupon_discount) lines.push(`🎟️ Coupon (${b.coupon_code}): – Rs.${Number(b.coupon_discount).toLocaleString()}`);
    lines.push('');
    lines.push(`🧾 *Grand Total: Rs.${grand.toLocaleString()}*`);
    lines.push('');
    lines.push(`📅 *Booking placed on:* ${fmtDateTime(b.created_at || b.booked_on)}`);
    if (b.advance_paid_at || b.first_payment_at) {
      lines.push(`💳 *Advance paid on:* ${fmtDateTime(b.advance_paid_at || b.first_payment_at)}`);
    }
    lines.push(`💳 *Balance paid on:* ${fmtDateTime(balancePaidAt)}`);
    lines.push('');
    const advanceAmt = Math.max(0, grand - paidNow);
    lines.push(`✅ *Advance (paid earlier):* Rs.${advanceAmt.toLocaleString()}`);
    lines.push(`✅ *Balance (paid now):* Rs.${paidNow.toLocaleString()}`);
    lines.push(`✅ *Outstanding:* Rs.0 — Fully Paid`);
    lines.push('');
    if (paymentId) lines.push(`🔖 *Balance Payment ID:* ${paymentId}`);
    if (b.payment_id || b.razorpay_payment_id) lines.push(`🔖 *Advance Payment ID:* ${b.payment_id || b.razorpay_payment_id}`);
    lines.push('');
    lines.push(`Confirmed from royalgardenvillas.in`);
    return lines.join('\n');
  };

  const sendBalanceReceiptToTeam = (phone) => {
    if (!balanceSuccess) return;
    const message = buildBalanceReceipt(balanceSuccess);
    const cleanPhone = String(phone).replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  if (!user) return null;

  return (
    <div data-testid="user-dashboard" className="pt-28 min-h-screen bg-[#F7F8FA]">
      <section className="py-10 md:py-14 bg-[#1B2432]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-2">Dashboard</p>
              <h1 className="text-3xl md:text-4xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Welcome, <span className="text-[#F57C20] italic">{user.full_name}</span>
              </h1>
            </div>
            <button onClick={handleLogout} data-testid="logout-btn" className="border-2 border-white/20 text-white px-5 py-2 text-xs font-semibold tracking-wider uppercase rounded-lg hover:border-[#F57C20] hover:text-[#F57C20] transition-all">
              Logout
            </button>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
            <Link to="/villas" className="bg-white border border-gray-100 rounded-xl p-6 hover:border-[#F57C20]/30 hover:shadow-lg transition-all group flex items-center gap-4">
              <div className="w-12 h-12 bg-[#F57C20]/10 rounded-lg flex items-center justify-center">
                <FiHome className="w-5 h-5 text-[#F57C20]" />
              </div>
              <div>
                <h3 className="text-[#1A1A2E] font-bold text-sm group-hover:text-[#F57C20] transition-colors">Browse Villas</h3>
                <p className="text-[#6B7280] text-xs">Explore our luxury villa collection</p>
              </div>
            </Link>
            <Link to="/booking" className="bg-white border border-gray-100 rounded-xl p-6 hover:border-[#F57C20]/30 hover:shadow-lg transition-all group flex items-center gap-4">
              <div className="w-12 h-12 bg-[#F57C20]/10 rounded-lg flex items-center justify-center">
                <FiCalendar className="w-5 h-5 text-[#F57C20]" />
              </div>
              <div>
                <h3 className="text-[#1A1A2E] font-bold text-sm group-hover:text-[#F57C20] transition-colors">Book a Villa</h3>
                <p className="text-[#6B7280] text-xs">Check availability and book your stay</p>
              </div>
            </Link>
          </div>

          {/* My Bookings */}
          <h2 className="text-xl text-[#1A1A2E] font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            My Bookings
          </h2>

          {error && (
            <div data-testid="dashboard-error" className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5 mb-4">
              {error}
            </div>
          )}

          {bookings.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl p-8 text-center">
              <p className="text-[#6B7280] text-sm mb-4">You don&apos;t have any bookings yet.</p>
              <Link to="/booking" className="text-[#F57C20] text-sm font-semibold hover:underline">Book your first stay</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map(b => {
                const grand = computeGrandTotal(b);
                const paid = effectivePaid(b);
                const outstanding = Math.max(0, grand - paid);
                const isCancelled = b.booking_status === 'cancelled';
                return (
                  <div key={b.id} data-testid={`booking-card-${b.id.slice(0, 8)}`} className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#6B7280] mb-1">Booking #{b.id.slice(0, 8)}</p>
                        <div className="space-y-1">
                          {b.items?.length ? b.items.map((item, i) => (
                            <p key={i} className="text-sm text-[#1A1A2E] font-medium">
                              {item.villa_name} &middot; {item.check_in} to {item.check_out}
                            </p>
                          )) : (
                            <p className="text-sm text-[#1A1A2E] font-medium">
                              {b.villa_name} &middot; {b.check_in} to {b.check_out}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 text-xs">
                          <div>
                            <p className="text-[#9CA3AF]">Total</p>
                            <p className="text-[#1A1A2E] font-bold">Rs.{grand.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[#9CA3AF]">Paid</p>
                            <p className="text-green-600 font-bold">Rs.{paid.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[#9CA3AF]">Outstanding</p>
                            <p className={`font-bold ${outstanding > 0 ? 'text-[#F57C20]' : 'text-green-600'}`} data-testid={`outstanding-${b.id.slice(0, 8)}`}>
                              Rs.{outstanding.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <span className={`text-[10px] px-3 py-1.5 rounded-lg font-semibold tracking-wider uppercase ${
                          b.booking_status === 'confirmed' ? 'bg-green-50 text-green-600' :
                          b.booking_status === 'cancelled' ? 'bg-red-50 text-red-600' :
                          'bg-yellow-50 text-yellow-700'
                        }`}>
                          {b.booking_status?.charAt(0).toUpperCase() + b.booking_status?.slice(1)}
                        </span>

                        {outstanding > 0 && !isCancelled && (
                          <button
                            onClick={() => handlePayBalance(b)}
                            disabled={payingId === b.id}
                            data-testid={`pay-balance-${b.id.slice(0, 8)}`}
                            className="bg-[#F57C20] text-white px-4 py-2 text-xs font-bold rounded-lg hover:bg-[#E06A10] transition-all hover:shadow-lg disabled:opacity-50 flex items-center gap-2"
                          >
                            <FiDollarSign className="w-3.5 h-3.5" />
                            {payingId === b.id ? 'Opening...' : `Pay Rs.${outstanding.toLocaleString()}`}
                          </button>
                        )}
                        {outstanding === 0 && paid > 0 && (
                          <span className="text-green-600 text-[10px] font-semibold flex items-center gap-1">
                            <FiCheckCircle className="w-3.5 h-3.5" /> Fully Paid
                          </span>
                        )}
                      </div>
                    </div>

                    {outstanding > 0 && !isCancelled && (
                      <p className="text-[#9CA3AF] text-[11px] mt-3 italic">
                        Balance must be paid by 1 PM, one day before check-in. Pay online instantly via Razorpay — UPI, Cards, NetBanking accepted.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* BALANCE PAYMENT SUCCESS MODAL */}
      {balanceSuccess && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-3" data-testid="balance-success-modal">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <FiCheck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1A1A2E]">Balance Payment Successful</p>
                  <p className="text-[10px] text-[#6B7280]">Booking fully paid — your villa is locked in</p>
                </div>
              </div>
              <button onClick={() => setBalanceSuccess(null)} data-testid="balance-success-close" className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
                <FiX className="w-4 h-4 text-[#6B7280]" />
              </button>
            </div>

            <div className="p-5">
              <div className="bg-gradient-to-br from-green-50 to-green-100/40 border border-green-200 rounded-xl p-4 mb-4">
                <p className="text-[10px] uppercase tracking-wider text-green-700 font-bold mb-1">Booking Ref</p>
                <p className="text-xl font-black tracking-widest text-[#1A1A2E]" style={{ fontFamily: 'monospace' }}>
                  {(balanceSuccess.booking.id || '').slice(0, 8).toUpperCase()}
                </p>
                <p className="text-[#1A1A2E] text-sm mt-2 font-semibold">{balanceSuccess.booking.villa_name || 'Your villa'}</p>
                <p className="text-[#6B7280] text-xs">
                  Balance paid: <strong className="text-green-700">Rs.{balanceSuccess.paidNow.toLocaleString()}</strong>
                </p>
              </div>

              <div className="bg-[#FFF8F0] border border-[#F57C20]/20 rounded-xl p-4 mb-4">
                <p className="text-[#1A1A2E] text-sm font-bold mb-1.5 flex items-center gap-1.5">
                  <FaWhatsapp className="w-4 h-4 text-[#25D366]" />
                  Notify RGV team on WhatsApp
                </p>
                <p className="text-[#6B7280] text-[11px] mb-3 leading-snug">
                  Forward your complete booking receipt — guest details, villa, dates, full pricing breakdown, payment IDs — so our team can prepare for your arrival.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => sendBalanceReceiptToTeam('919820088292')}
                    data-testid="balance-send-office-btn"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] active:bg-[#179a4b] transition-colors text-white text-sm font-semibold px-5 py-3 rounded-xl shadow-md"
                  >
                    <FaWhatsapp className="w-5 h-5" />
                    Send to RGV Office (98200 88292)
                  </button>
                  <button
                    onClick={() => sendBalanceReceiptToTeam('919137893277')}
                    data-testid="balance-send-manager-btn"
                    className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 transition-colors text-[#1B2432] text-sm font-semibold px-5 py-3 rounded-xl shadow-sm border-2 border-[#25D366]"
                  >
                    <FaWhatsapp className="w-5 h-5 text-[#25D366]" />
                    Send to Manager (91378 93277)
                  </button>
                </div>
                <p className="text-[10px] text-center text-[#9CA3AF] mt-2 italic">Recommended — helps us serve you better</p>
              </div>

              <button
                onClick={() => setBalanceSuccess(null)}
                data-testid="balance-success-dismiss"
                className="w-full text-[#6B7280] text-xs py-2 hover:text-[#1A1A2E]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
